import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Skeleton } from "@/components/ui/skeleton";

import { useSupabase } from "./SupabaseProvider";

/**
 * Handles the post-OAuth redirect from Supabase. The provider redirects
 * here with `?code=<oauth_code>&next=<path>`; we exchange the code for a
 * session, then navigate to `next` (or `/jobs`).
 *
 * `detectSessionInUrl: false` on the client is intentional — handling the
 * exchange explicitly here keeps the auth flow visible (one component,
 * one effect) instead of magic-mounting.
 *
 * `exchangeCodeForSession` takes the bare auth code, not the callback
 * URL. supabase-js does no URL parsing here — passing `window.location.href`
 * sends the entire URL as the `auth_code` field, which GoTrue then
 * fails to find in `auth.flow_state` and returns `flow_state_not_found`.
 *
 * The ref guard is load-bearing for PKCE: React StrictMode double-invokes
 * `useEffect` on mount, and `exchangeCodeForSession` is single-use — the
 * first call consumes the PKCE verifier from localStorage and the second
 * fails with "code verifier not found in storage". Gating on a ref keeps
 * the exchange truly once-per-page-load.
 */
export function AuthCallback() {
  const { client } = useSupabase();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    const next = params.get("next") ?? "/jobs";
    const code = params.get("code");
    if (!code) {
      setError("Missing OAuth code in callback URL.");
      return;
    }
    client.auth.exchangeCodeForSession(code).then(({ error: err }) => {
      if (err) {
        setError(err.message);
        return;
      }
      navigate(next, { replace: true });
    });
  }, [client, navigate, params]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center space-y-2">
          <p className="text-red-300">Sign-in failed: {error}</p>
          <a href="/sign-in" className="text-accent underline">
            Try again
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Skeleton className="h-10 w-48" />
    </div>
  );
}
